"use client";

import React, { useState, useEffect } from "react";

interface AdvancedInputsProps {
    propertyTax: number;
    setPropertyTax: (value: number) => void;
    insurance: number;
    setInsurance: (value: number) => void;
}

const formatWithCommas = (value: number | string): string => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const parseNumber = (value: string): number => Number(value.replace(/,/g, ""));

const AdvancedInputs: React.FC<AdvancedInputsProps> = ({ propertyTax, setPropertyTax, insurance, setInsurance }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [propertyTaxInput, setPropertyTaxInput] = useState("0");
    const [insuranceInput, setInsuranceInput] = useState("0");

    useEffect(() => {
        setPropertyTaxInput(formatWithCommas(propertyTax));
    }, [propertyTax]);

    useEffect(() => {
        setInsuranceInput(formatWithCommas(insurance));
    }, [insurance]);

    return (
        <div className="border border-gray-300 overflow-hidden shadow 2xl:w-[900px]">
            <button
                type="button"
                className="w-full text-left bg-[#202358] text-white px-4 py-3 cursor-pointer select-none flex items-center justify-between hover:bg-blue active:bg-blue focus:bg-blue 2xl:h-[42px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <h5 className="text-lg font-bold">ADVANCED</h5>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="bg-white p-3 2xl:w-[898px] space-y-2">
                    {/* Property Tax */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-center 2xl:gap-4 ">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="propertyTax"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[124px] content-center"
                                >
                                    Property Tax /Yr: $
                                </label>
                                <input
                                    id="propertyTax"
                                    type="text"
                                    inputMode="numeric"
                                    className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] bg-[#f6f9ff] text-[14px]"
                                    value={propertyTaxInput}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/,/g, "");
                                        if (/^\d*$/.test(raw)) {
                                            const val = Number(raw);
                                            setPropertyTax(val);
                                            setPropertyTaxInput(formatWithCommas(raw));
                                        }
                                    }}
                                    onBlur={() => {
                                        setPropertyTaxInput(formatWithCommas(propertyTax));
                                    }}
                                />
                            </div>
                            <input
                                aria-label="Property tax per year"
                                type="range"
                                min={0}
                                max={1000000}
                                step={1000}
                                value={propertyTax}
                                onChange={(e) => setPropertyTax(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0 ">
                                <span>$0</span>
                                <span>$25K</span>
                                <span>$50K</span>
                                <span>$100K</span>
                                <span>$1M</span>
                            </div>
                        </div>
                    </div>

                    {/* Insurance */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-center 2xl:gap-4 ">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="insurance"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[106px] content-center"
                                >
                                    Insurance /Yr: $
                                </label>
                                <input
                                    id="insurance"
                                    type="text"
                                    inputMode="numeric"
                                    className="border px-3 py-1 rounded w-36 text-left 2xl:w-[122px] bg-[#f6f9ff] text-[14px]"
                                    value={insuranceInput}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/,/g, "");
                                        if (/^\d*$/.test(raw)) {
                                            const val = Number(raw);
                                            setInsurance(val);
                                            setInsuranceInput(formatWithCommas(raw));
                                        }
                                    }}
                                    onBlur={() => {
                                        setInsuranceInput(formatWithCommas(insurance));
                                    }}
                                />
                            </div>
                            <input
                                aria-label="Homeowners insurance per year"
                                type="range"
                                min={0}
                                max={1000000}
                                step={1000}
                                value={insurance}
                                onChange={(e) => setInsurance(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0 ">
                                <span>$0</span>
                                <span>$25K</span>
                                <span>$50K</span>
                                <span>$100K</span>
                                <span>$1M</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdvancedInputs;
